<template>
  <li class="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-border-0 tw-border-t tw-border-solid tw-border-gray-100 tw-px-6 tw-py-3">
    <div class="tw-flex tw-min-w-0 tw-items-start tw-gap-2.5">
      <WdsIcon v-if="isConfirmed" name="checkmark-filled" class="tw-flex-none tw-text-green-500" />
      <WdsIcon v-else name="close" class="tw-flex-none tw-text-gray-600" />

      <div class="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
        <WdsText>{{ check.label }}</WdsText>

        <div class="tw-flex tw-items-center tw-gap-1.5">
          <WdsText v-if="isConfirmed" secondary>{{ $t('Confirmed by :name', { name: deciderName }) }}</WdsText>
          <WdsText v-else secondary>{{ $t('Marked not applicable by :name', { name: deciderName }) }}</WdsText>
          <WdsText v-if="check.decidedAt" tertiary><BaseRelativeTime :date="check.decidedAt" /></WdsText>
        </div>

        <div
          v-if="!isConfirmed && check.notApplicableReason"
          class="tw-mt-1 tw-flex tw-items-start tw-gap-1.5"
          data-test="checklist-not-applicable-reason-text"
        >
          <WdsText secondary class="tw-flex-none">
            <span class="tw-flex tw-h-[1lh] tw-items-center">
              <WdsIcon name="chat" :size="14" class="tw-text-gray-600" />
            </span>
          </WdsText>
          <WdsText secondary class="tw-min-w-0">{{ check.notApplicableReason }}</WdsText>
        </div>
      </div>
    </div>

    <div class="tw-flex tw-min-h-5 tw-flex-none tw-items-center">
      <WdsSpinner v-if="loading" data-test="checklist-check-saving" indeterminate />
      <WdsButtonText
        v-else-if="canDecide"
        data-test="checklist-set-pending"
        @click="setState(ChecklistCheckState.Pending)"
      >
        {{ $t('Undo') }}
      </WdsButtonText>
    </div>
  </li>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { WdsButtonText, WdsIcon, WdsSpinner, WdsText } from '@worksome/wds-components'
import { ChecklistCheckRowFragment } from './fragments'
import { useChecklistCheck } from './useChecklistCheck'
import BaseRelativeTime from '@/components/BaseRelativeTime.vue'
import { FragmentType } from '@/gql'
import { ChecklistCheckState } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

const props = defineProps<{
  check: FragmentType<typeof ChecklistCheckRowFragment>
  canDecide: boolean
}>()

const { check, loading, setState } = useChecklistCheck(() => props.check)
const isConfirmed = computed(() => check.value.state === ChecklistCheckState.Confirmed)

// Every decision records a user, so a decision without one was made by someone whose account is gone.
const deciderName = computed(() => check.value.decidedBy?.name ?? $t('Deleted user'))
</script>
